import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  forwardRef,
  input,
  InputSignal,
  linkedSignal,
  resource,
  signal,
  viewChild
} from '@angular/core';
import { FilterInputBase } from '../filter-input.base';
import { FilterItemConfig } from '../../../../types/filter-config';
import {
  Entity,
  QueryColumnFilter,
  QueryFilter,
  QueryNumberSetFilter,
  QueryStringSetFilter,
  uuid
} from '@softline/core';
import { JsonPipe } from '@angular/common';
import { CheckboxComponent, IconComponent, IsDefinedNotEmptyPipe, LoadingSkeletonComponent, LoadingSpinnerComponent, TranslatePipe, translator } from '@softline/ui-core';
import { DsTabsComponent } from '@softline/ui-core/tabs';
import { BehaviorSubject, debounceTime } from 'rxjs';
import { toSignal } from '@angular/core/rxjs-interop';
import { Filter } from '../../../../types/filter';
import { PageFilterDataService } from '../../../services/data-source/page-filter-data.service';

export const ITEMS_PER_PAGE = 50;

@Component({
  selector: 'soft-multi-select-filter',
  imports: [
    JsonPipe,
    DsTabsComponent,
    LoadingSpinnerComponent,
    CheckboxComponent,
    IconComponent,
    TranslatePipe,
    LoadingSkeletonComponent,
    IsDefinedNotEmptyPipe
  ],
  templateUrl: './multi-select-filter-input.component.html',
  styleUrl: './multi-select-filter-input.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {provide: FilterInputBase, useExisting: forwardRef(() => MultiSelectFilterInputComponent)}
  ]
})
export class MultiSelectFilterInputComponent<T extends object, TValue> extends FilterInputBase<T, 'in' | 'notIn'> {
  private t = translator();

  readonly page = signal<number>(0);

  readonly search$ = new BehaviorSubject<string>('');
  readonly debouncedSearch$ = this.search$.pipe(debounceTime(300));
  readonly search = toSignal(this.debouncedSearch$, {initialValue: ''});

  override readonly item: InputSignal<FilterItemConfig<T>> = input.required<FilterItemConfig<T>>();
  override readonly itemFilter = input.required<Filter<T> & {operator: 'in' | 'notIn'} | null>();
  override readonly operator = input.required<'in' | 'notIn'>();
  override readonly dataService = input.required<PageFilterDataService<T>>();
  override readonly queryFilter = input.required<QueryFilter<T> | null>();

  readonly loadingRows = signal<number[]>([1, 2, 3, 4, 5, 6, 7, 8, 9, 10]);

  readonly view = signal<'all' | 'selected'>('all');
  readonly viewTabs = computed<
    readonly { title: string; value: 'all' | 'selected'; badge?: string | number }[]
  >(() => {
    const selected = this.selectedValues().length;
    return [
      {
        title: this.t('#CORE_FILTER.MULTI_SELECT.ALL')(),
        value: 'all',
      },
      {
        title: this.t('#CORE_FILTER.MULTI_SELECT.SELECTED')(),
        value: 'selected',
        badge: selected > 0 ? selected : undefined,
      },
    ];
  });

  selectedValues = linkedSignal(() => {
    const filter = this.itemFilter();
    if(!filter)
      return [];
    return filter.value
  })

  // resource()'s own `equal` only gates its resolved value, not whether the loader re-fires -
  // that's decided by comparing the params() result via ===. Memoize it here so an equal-but-
  // new-reference params object (e.g. from a parent that rebuilds queryFilter on every CD
  // cycle) doesn't re-trigger the loader.
  private static readonly requestParamsEqual = <T extends object>(
    a: { item: FilterItemConfig<T>; filter: QueryFilter<T> | null; page: number; search: string },
    b: { item: FilterItemConfig<T>; filter: QueryFilter<T> | null; page: number; search: string }
  ): boolean =>
    a.item === b.item &&
    a.page === b.page &&
    a.search === b.search &&
    JSON.stringify(a.filter) === JSON.stringify(b.filter);

  private readonly requestParams = computed(
    () => ({item: this.item(), filter: this.queryFilter(), page: this.page(), search: this.search()}),
    {equal: MultiSelectFilterInputComponent.requestParamsEqual}
  );

  valuesResource = resource({
    params: () => this.requestParams(),
    loader: async ({ params }) => {
      const start = params.page * ITEMS_PER_PAGE
      const end = (params.page + 1) * ITEMS_PER_PAGE
      return await this.dataService().loadItems(params.item, params.filter, start, end, params.search);
    },
    defaultValue: []
  });

  values = linkedSignal<any, TValue[]>({
    source: () => this.valuesResource.value(),
    computation: (response, previous) => {
      const prev = previous?.value ?? [];
      return response ? [...prev, ...response] : prev;
    },
  });

  // A full window came back -> there may be more pages to load.
  readonly hasMore = computed(
    () => (this.valuesResource.value()?.length ?? 0) >= ITEMS_PER_PAGE
  );

  readonly searchInput = viewChild<ElementRef<HTMLInputElement>>('searchInput');
  readonly pickerList = viewChild<ElementRef<HTMLElement>>('pickerList');

  // Index of the keyboard-highlighted option within the currently displayed
  // list; -1 means nothing is highlighted yet.
  readonly highlightedIndex = signal<number>(-1);

  // The list the keyboard navigation operates on, matching the active tab.
  readonly displayedValues = computed<TValue[]>(() =>
    this.view() === 'selected'
      ? this.selectedValues()
      : this.values()
  );

  constructor() {
    super();
    // Whenever the query (search or filters) changes, restart pagination
    // from the first page and drop the accumulated windows.
    effect(() => {
      this.search();
      this.itemFilter();
      this.page.set(0);
      this.values.set([]);
    });
    // Reset the keyboard highlight whenever the visible list changes
    // (typing a search or switching tabs).
    effect(() => {
      this.search();
      this.view();
      this.highlightedIndex.set(-1);
    });
    // Focus the search field on open so the user can type immediately.
    afterNextRender(() => this.searchInput()?.nativeElement.focus());
  }

  isSelected(value: TValue): boolean {
    const selectedValues = this.selectedValues();
    if (this.item().type === 'entity')
      return selectedValues.some((v: any) => v.id === (value as Entity).id);
    return selectedValues.some((v) => v === value);
  }

  toggleValue(value: TValue): void {
    let currentValue: unknown | null = null;
    const selectedValues = this.selectedValues();
    if(this.item().type === 'entity')
      currentValue = selectedValues.find((v: any) => v.id === (value as Entity).id);
    else {
      currentValue = selectedValues.find((v) => v === value);
    }
    if(currentValue)
      this.selectedValues.update((prev) => prev.filter((v) => v !== currentValue));
    else
      this.selectedValues.update((prev) => [...prev, value]);
  }

  onPickerSearchClear(): void {
    this.search$.next('');
  }

  onSearchInput(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.search$.next(value);
  }

  onSearchKeydown(event: KeyboardEvent): void {
    switch (event.key) {
      case 'Escape': {
        const input = event.target as HTMLInputElement;
        if (input.value) {
          event.preventDefault();
          input.value = '';
          this.search$.next('');
          this.highlightedIndex.set(-1);
        }
        break;
      }
      case 'Enter': {
        event.preventDefault();
        const list = this.displayedValues();
        const index = this.highlightedIndex();
        const value =
          index >= 0 && index < list.length ? list[index] : list[0];
        if (value !== undefined)
          this.toggleValue(value);
        break;
      }
      case 'ArrowDown':
        event.preventDefault();
        this.moveHighlight(1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.moveHighlight(-1);
        break;
    }
  }

  private moveHighlight(delta: number): void {
    const list = this.displayedValues();
    if (list.length === 0) {
      this.highlightedIndex.set(-1);
      return;
    }
    const next = Math.min(
      Math.max(this.highlightedIndex() + delta, 0),
      list.length - 1
    );
    this.highlightedIndex.set(next);
    this.scrollHighlightedIntoView(next);
  }

  private scrollHighlightedIntoView(index: number): void {
    const container = this.pickerList()?.nativeElement;
    const el = container?.querySelector<HTMLElement>(
      `[data-option-index="${index}"]`
    );
    el?.scrollIntoView({ block: 'nearest' });
  }

  onScroll(event: Event): void {
    const el = event.target as HTMLElement;
    if (this.view() !== 'all')
      return;
    if (this.valuesResource.isLoading())
      return;
    if (!this.hasMore())
      return;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 80) {
      this.page.set(this.page() + 1);
    }
  }

  override getFilterValue(): Filter<T> | null {
    const item = this.item();
    const selectedValues = this.selectedValues();

    if(selectedValues.length === 0)
      return null;

    return {
      uuid: uuid(),
      field: item.field,
      type: item.type as any,
      operator: this.operator(),
      value: selectedValues
    }
  }
}
